import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import {
  type CarregamentoDasSecoesDoCertificado,
  type CertificadoNegociacaoState,
  type EstadoDoCertificadoNegociacao,
  type PedidoDePaginaDoCertificado,
  SECOES_DO_CERTIFICADO,
  type SecaoCarregadaDoCertificado,
  type SecaoDoDocumentoDoCertificado,
  type SecoesDoCertificadoState,
  type SecoesDoDocumentoDoCertificado,
} from '../types/certificadoNegociacaoTypes';
import { agruparMensagensEmConversas } from '../utils/certificadoNegociacao';
import type { MarcosState, MarcosViewState } from '../types/negociacaoMarcoTypes';

const TENTATIVAS_PARA_SUGERIR_SUPORTE = 3;

import type { NegociacaoCasoTelefoneUi } from '../types/negociacaoDetailTypes';

/** Referência estável para lista vazia: devolver `[]` novo a cada chamada rerenderiza tudo. */
const VAZIO: NegociacaoCasoTelefoneUi[] = [];

export const selectNegociacaoDetailCaso = (state: RootState) => state.negociacaoDetail?.caso;

const selectMarcosState = (state: RootState): MarcosState | undefined =>
  state.negociacaoDetail?.marcos;

export const selectMarcosViewState = createSelector(
  [selectMarcosState],
  (marcos): MarcosViewState => {
    const semItens = !marcos || marcos.items.length === 0;

    if (!marcos || marcos.status === 'idle' || (marcos.status === 'loading' && semItens)) {
      return { estado: 'carregando' };
    }

    if (marcos.status === 'failed') {
      return {
        estado: 'erro',
        mensagem: marcos.error ?? 'Ocorreu um erro ao buscar os marcos desta negociação.',
        sugerirSuporte: marcos.tentativas >= TENTATIVAS_PARA_SUGERIR_SUPORTE,
      };
    }

    if (marcos.items.length === 0) {
      return { estado: 'vazio' };
    }

    return {
      estado: 'lista',
      items: marcos.items,
      hasMore: marcos.hasMore,
      carregandoMais: marcos.status === 'loading',
    };
  },
);

export const selectMarcosNextCursor = (state: RootState) =>
  state.negociacaoDetail?.marcos.nextCursor ?? null;
export const selectMarcosCarregando = (state: RootState) =>
  state.negociacaoDetail?.marcos.status === 'loading';
export const selectNegociacaoDetailConversaId = (state: RootState) =>
  state.negociacaoDetail?.conversaId;
export const selectNegociacaoTelefones = (state: RootState) =>
  state.negociacaoDetail?.telefones.items ?? VAZIO;
export const selectNegociacaoTelefonesLoading = (state: RootState) =>
  state.negociacaoDetail?.telefones.isLoading ?? false;
export const selectNegociacaoTelefonesError = (state: RootState) =>
  state.negociacaoDetail?.telefones.error ?? null;
export const selectNegociacaoTelefonesCasoIdBuscado = (state: RootState) =>
  state.negociacaoDetail?.telefones.casoIdBuscado ?? null;

/**
 * O canal que a aba Chat mostra: o escolhido pelo operador ou, na ausência, o canal ATIVO do
 * caso. Um seletor só, para nenhum componente reimplementar esse fallback.
 */
export const selectCanalVisualizadoId = (state: RootState) =>
  state.negociacaoDetail?.canalVisualizadoId ?? state.negociacaoDetail?.conversaId ?? null;

export const selectTelefoneDoCanalVisualizado = createSelector(
  [selectNegociacaoTelefones, selectCanalVisualizadoId, selectNegociacaoDetailCaso],
  (telefones, canalId, caso) => {
    const doCanal = telefones.find((telefone) => telefone.conversaId === canalId);
    // Caso legado (sem fila) cai no telefone do próprio caso — é o comportamento de antes.
    return doCanal?.numeroNormalizado ?? caso?.counterpartyPhone ?? null;
  },
);

export const selectLexiProcessing = (state: RootState) =>
  state.negociacaoDetail?.lexiProcessing ?? false;
export const selectLexiProcessingFailed = (state: RootState) =>
  state.negociacaoDetail?.lexiProcessingFailed ?? false;
export const selectJanelaAtendimento = (state: RootState) =>
  state.negociacaoDetail?.caso?.janelaAtendimento ?? null;
export const selectNegociacaoDetailAiInsights = (state: RootState) =>
  state.negociacaoDetail?.aiInsights || null;

export const selectAssinaturaAcordoPainel = (state: RootState) =>
  state.negociacaoDetail?.assinaturaAcordo.data ?? null;

export const selectFluxoDaNegociacao = (state: RootState) => state.negociacaoDetail.fluxo;

export const selectNegociacaoDetailLoading = (state: RootState) =>
  state.negociacaoDetail?.loading || false;
export const selectNegociacaoDetailError = (state: RootState) => state.negociacaoDetail?.error;
export const selectNegociacaoDetailAiInsightsLoading = (state: RootState) =>
  state.negociacaoDetail?.isLoadingAiInsights || false;
export const selectNegociacaoDetailAiInsightsError = (state: RootState) =>
  state.negociacaoDetail?.aiInsightsError || null;

export const selectNegociacaoDetailCancelStart = (state: RootState) => ({
  isCancellingStart: state.negociacaoDetail?.isCancellingStart || false,
  cancelStartError: state.negociacaoDetail?.cancelStartError || null,
});

export const selectNegociacaoDetailIsUpdating = (state: RootState) =>
  state.negociacaoDetail?.isUpdating || false;
export const selectNegociacaoDetailUpdateError = (state: RootState) =>
  state.negociacaoDetail?.updateError || null;

export const selectNegociacaoDetailIsDownloadingReport = (state: RootState) =>
  state.negociacaoDetail?.isDownloadingReport || false;

export const selectNegociacaoDetailIsEncerrando = (state: RootState) =>
  state.negociacaoDetail?.isEncerrando || false;
export const selectNegociacaoDetailEncerramentoError = (state: RootState) =>
  state.negociacaoDetail?.encerramentoError || null;
export const selectNegociacaoDetailIsEncerrandoAtendimento = (state: RootState) =>
  state.negociacaoDetail?.isEncerrandoAtendimento || false;
export const selectNegociacaoDetailIsReabrindo = (state: RootState) =>
  state.negociacaoDetail?.isReabrindo || false;
export const selectNegociacaoDetailReaberturaError = (state: RootState) =>
  state.negociacaoDetail?.reaberturaError || null;

export const selectIsResumingJanela = (state: RootState) =>
  state.negociacaoDetail?.isResumingJanela || false;
export const selectResumeJanelaError = (state: RootState) =>
  state.negociacaoDetail?.resumeJanelaError || null;
export const selectResumeJanelaCaseId = (state: RootState) =>
  state.negociacaoDetail?.resumeJanelaCaseId ?? null;

export const selectOutrasNegociacoes = (state: RootState) =>
  state.negociacaoDetail.outrasNegociacoes.items;
export const selectOutrasNegociacoesIsLoading = (state: RootState) =>
  state.negociacaoDetail.outrasNegociacoes.isLoading;
export const selectOutrasNegociacoesError = (state: RootState) =>
  state.negociacaoDetail.outrasNegociacoes.error;
export const selectOutrasNegociacoesPage = (state: RootState) =>
  state.negociacaoDetail.outrasNegociacoes.page;
export const selectOutrasNegociacoesTotal = (state: RootState) =>
  state.negociacaoDetail.outrasNegociacoes.total;
export const selectOutrasNegociacoesTotalPages = (state: RootState) =>
  state.negociacaoDetail.outrasNegociacoes.totalPages;

const CERTIFICADO_CARREGANDO: EstadoDoCertificadoNegociacao = { fase: 'carregando' };

const selectCertificadoState = (state: RootState): CertificadoNegociacaoState | undefined =>
  state.negociacaoDetail?.certificado;

/**
 * A emissão do caso pedido. Antes do primeiro pedido, durante o pedido e com o estado de outro
 * caso, a resposta é "carregando": nunca uma contagem zerada (CA-20).
 */
export const selectEstadoDoCertificadoNegociacao = createSelector(
  [selectCertificadoState, (_state: RootState, casoId: string) => casoId],
  (certificado, casoId): EstadoDoCertificadoNegociacao => {
    if (!certificado || certificado.casoId !== casoId || certificado.loading) {
      return CERTIFICADO_CARREGANDO;
    }
    if (certificado.error) return { fase: 'erro', erro: certificado.error };
    if (certificado.dados) return { fase: 'pronto', certificado: certificado.dados };
    return CERTIFICADO_CARREGANDO;
  },
);

export const selectCertificadoNegociacaoBaixandoPdf = (state: RootState) =>
  state.negociacaoDetail?.certificado.baixandoPdf ?? false;

export const selectAcontecimentosDoCertificado = (state: RootState): number =>
  state.negociacaoDetail?.certificado.acontecimentos ?? 0;

const selectSecoesDoCertificadoState = (state: RootState): SecoesDoCertificadoState | undefined =>
  state.negociacaoDetail?.certificado.secoes;

const selectEmissaoIdArg = (_state: RootState, emissaoId: string) => emissaoId;

/** As seções da emissão pedida; de outra emissão (ou nenhuma), nada foi carregado ainda. */
const selectSecoesDaEmissao = createSelector(
  [selectSecoesDoCertificadoState, selectEmissaoIdArg],
  (secoes, emissaoId): SecoesDoCertificadoState | null =>
    secoes?.emissaoId === emissaoId ? secoes : null,
);

const completa = ({
  iniciada,
  hasMore,
}: Pick<SecaoCarregadaDoCertificado<unknown>, 'iniciada' | 'hasMore'>): boolean =>
  iniciada && !hasMore;

const naoIniciada = <T>(): SecaoDoDocumentoDoCertificado<T> => ({ items: [], completa: false });

const SECOES_NAO_INICIADAS: SecoesDoDocumentoDoCertificado = {
  campos: naoIniciada(),
  contatos: naoIniciada(),
  marcos: naoIniciada(),
  conversas: naoIniciada(),
};

/** O que o documento da emissão já pode mostrar, com as mensagens agrupadas por bloco. */
export const selectSecoesDoDocumentoDoCertificado = createSelector(
  [selectSecoesDaEmissao],
  (secoes): SecoesDoDocumentoDoCertificado => {
    if (!secoes) return SECOES_NAO_INICIADAS;
    return {
      campos: { items: secoes.campos.items, completa: completa(secoes.campos) },
      contatos: { items: secoes.contatos.items, completa: completa(secoes.contatos) },
      marcos: { items: secoes.marcos.items, completa: completa(secoes.marcos) },
      conversas: {
        items: agruparMensagensEmConversas(secoes.mensagens.items),
        completa: completa(secoes.mensagens),
      },
    };
  },
);

/**
 * A próxima página a pedir, na ordem do documento: a seção seguinte só começa quando a anterior
 * acabou. Ignora carregamento e falha (quem decide se pede é o hook), para o Tentar novamente
 * pedir exatamente a página que falhou.
 */
export const selectProximaPaginaDoCertificado = createSelector(
  [selectSecoesDaEmissao],
  (secoes): PedidoDePaginaDoCertificado | null => {
    if (!secoes) return { secao: SECOES_DO_CERTIFICADO[0], cursor: null };
    const secao = SECOES_DO_CERTIFICADO.find((nome) => !completa(secoes[nome]));
    if (!secao) return null;
    return { secao, cursor: secoes[secao].iniciada ? secoes[secao].nextCursor : null };
  },
);

export const selectCarregamentoDasSecoesDoCertificado = createSelector(
  [selectSecoesDaEmissao, selectProximaPaginaDoCertificado],
  (secoes, proxima): CarregamentoDasSecoesDoCertificado => {
    if (secoes?.falhou) return 'falhou';
    return proxima ? 'carregando' : 'completo';
  },
);

export const selectPaginaDoCertificadoEmVoo = (state: RootState): boolean =>
  state.negociacaoDetail?.certificado.secoes.carregando ?? false;
